import Skeleton from "react-loading-skeleton";
import { useModalControl } from "../../customHooks/useModalControl";
import { useDeleteMemberBankAccountMutation } from "../../redux/features/payment/payment.api";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../utils/constants";
import { Icon } from "../icons/icon";
import { ConfirmationModal } from "../modals/app_modals/confirmationModal";

export const SingleBankComponent = ({
  accountName,
  accountNumber,
  bankName,
  canDelete = false,
  accountId,
  onDeleteSuccess = () => {},
}: {
  accountName: string;
  accountNumber: string;
  bankName: string;
  canDelete?: boolean;
  accountId?: number;
  onDeleteSuccess?: () => void;
}) => {
  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();

  const [deleteMemberBankAccount, { isLoading: deletingMemberBankAccount }] =
    useDeleteMemberBankAccountMutation();

  return (
    <div
      className="
      flex flex-row flex-wrap
      gap-4 sm:gap-6 justify-between items-center
      py-[16px] px-[24px] rounded-[8px] bg-light-green-bg
      bg-[url('assets/images/card_bg.svg')] bg-no-repeat
      bg-cover md:bg-contain bg-center
      "
    >
      <Icon
        svg="bank-colored"
        className={"w-[32px] h-[32px] md:w-[28px] md:h-[48px]"}
      />

      <div className="flex flex-1 gap-4 items-center justify-between">
        <p className="flex flex-col gap-1">
          <span className="text-sm md:text-base font-medium opacity-80">
            {accountNumber}
          </span>

          <span className="text-xs md:text-sm opacity-80">{accountName}</span>

          <span className="text-xs md:text-sm opacity-50">{bankName}</span>
        </p>

        {canDelete && (
          <button
            className="
              bg-white p-2 rounded-full text-red-200 
              w-fit cursor-pointer border
              hover:scale-[0.95]
              hover:bg-lighter-red-bg
              hover:border-red-200
              transition-all duration-300
              "
            onClick={() => {
              handleModalOpen("confirmDeleteModal");
            }}
          >
            <Icon svg={"delete-icon"} width={20} height={20} className={""} />
          </button>
        )}
      </div>

      <ConfirmationModal
        isModalOpen={modalOpen && modal === "confirmDeleteModal"}
        onModalClose={() => {
          handleModalClose();
        }}
        title={"Confirm bank account deletion"}
        children={undefined}
        subtitle={
          "Are you sure you want to delete this bank account? This action cannot be undone."
        }
        onProceed={async () => {
          if (accountId) {
            const resp = await deleteMemberBankAccount({
              id: accountId,
            });

            if (resp.data?.success) {
              onDeleteSuccess();
              handleModalClose();
            }
          }
        }}
        loading={deletingMemberBankAccount}
        proceedButtonText="Proceed"
      />
    </div>
  );
};

export const BankLoader = () => {
  return (
    <div
      className="
      flex flex-row flex-wrap
      gap-4 sm:gap-6 justify-between items-center
      py-[16px] px-[24px] rounded-[8px] bg-light-green-bg
      "
    >
      <Skeleton
        width={32}
        height={32}
        baseColor={SKELETON_BASE_COLOR}
        highlightColor={SKELETON_HIGHLIGHT_COLOR}
      />

      <div className="flex flex-col w-full gap-1 flex-1 items-center justify-between">
        <Skeleton
          width={"80%"}
          height={24}
          baseColor={SKELETON_BASE_COLOR}
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          className="rounded-full"
          containerClassName="w-full"
        />

        <Skeleton
          width={"40%"}
          height={20}
          baseColor={SKELETON_BASE_COLOR}
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          className="rounded-full"
          containerClassName="w-full"
        />

        <Skeleton
          width={"20%"}
          height={20}
          baseColor={SKELETON_BASE_COLOR}
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          className="rounded-full"
          containerClassName="w-full"
        />
      </div>
    </div>
  );
};
